/**
 * useLifecycleData
 *
 * Thin frontend hook that fetches pre-computed lifecycle frames
 * from the backend. All heavy geometry computation (buffer+union,
 * clipping, fire history) happens server-side.
 */
import { useState, useEffect } from 'react';
import { WildfireEntity } from '../types/wildfire';
import { getBackendUrl } from '../services/configService';

/** A single day's polygon in the lifecycle timeline */
export interface LifecycleFrame {
  date: string;
  polygon: GeoJSON.MultiPolygon | null;
  source: 'gps' | 'hotspot';
  areaHa?: number;
}

export interface UseLifecycleDataResult {
  startDate: string | null;
  endDate: string | null;
  origin: [number, number] | null;
  frames: LifecycleFrame[];
  loading: boolean;
  error: string | null;
}

export function useLifecycleData(wildfire: WildfireEntity): UseLifecycleDataResult {
  const [state, setState] = useState<UseLifecycleDataResult>({
    startDate: null,
    endDate: null,
    origin: null,
    frames: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    const fireNumber = wildfire.fire_number;
    const startDate = wildfire.estimated_start_date ?? null;
    const outDate = wildfire.out_date ?? null;

    // Derive origin
    const mongoDoc = wildfire.data;
    const lat = mongoDoc?.latitudeDD;
    const lon = mongoDoc?.longitudeDD;
    const origin: [number, number] | null = lat != null && lon != null ? [lon, lat] : null;

    if (!startDate) {
      setState({
        startDate: null,
        endDate: null,
        origin,
        frames: [],
        loading: false,
        error: null,
      });
      return;
    }

    async function load() {
      try {
        const params = new URLSearchParams({ startDate: startDate! });
        if (outDate) params.set('outDate', outDate);
        if (origin) {
          params.set('lat', String(origin[1]));
          params.set('lon', String(origin[0]));
        }

        const url = `${getBackendUrl()}/api/lifecycle/${encodeURIComponent(fireNumber)}?${params.toString()}`;
        const response = await fetch(url, { credentials: 'include' });

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();

        if (cancelled) return;

        setState({
          startDate: result.startDate,
          endDate: result.endDate,
          origin: result.origin,
          frames: result.frames,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState(prev => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load lifecycle data',
        }));
      }
    }

    load();

    return () => { cancelled = true; };
  }, [wildfire.fire_number]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}
